import { AsyncPipe } from '@angular/common';
import {
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
  inject,
  signal,
} from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { CommoditiesService } from 'src/app/services/commodities/commodities.service';
import { MarketType } from '../../bids-and-offers/lots.model';
import { delay, map, of, switchMap } from 'rxjs';
import { Store } from '@ngrx/store';
import { UserState } from 'src/app/store/user/user.reducer';
import { UserService } from 'src/app/services/user.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatChipsModule } from '@angular/material/chips';
import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { MatIconModule } from '@angular/material/icon';
import { MatSelectModule } from '@angular/material/select';
import { ScrollingModule } from '@angular/cdk/scrolling';
import { getUserData } from 'src/app/store/user/user.actions';
import { selectUser } from 'src/app/store/user/user.selectors';
import { Analytics, logEvent } from '@angular/fire/analytics';
import { LogEventsEnum } from 'src/app/firebase.model';

@Component({
  selector: 'app-default-commodity-autocomplete',
  imports: [
    FormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatChipsModule,
    MatIconModule,
    MatSelectModule,
    MatAutocompleteModule,
    ScrollingModule,
    ReactiveFormsModule,
  ],
  templateUrl: './default-commodity-autocomplete.component.html',
  styleUrl: './default-commodity-autocomplete.component.css',
  standalone: true,
})
export class DefaultCommodityAutocompleteComponent
  implements OnInit, OnDestroy
{
  @ViewChild('input') input: ElementRef<HTMLInputElement>;
  private analytics = inject(Analytics);

  favouriteCommodityInput: ElementRef<HTMLInputElement>;
  newDefaultCommodity = new FormControl('');
  favCommodityCtrl = new FormControl([]);

  separatorKeysCodes: number[] = [ENTER, COMMA];

  defaultOptions = signal<string[]>([]);
  favouriteOptions = signal<string[]>([]);

  filteredOptions: string[];
  defaultOrigin = signal<string>(null);
  defaultMarketType = signal<string>(null);
  defaultCommodity = signal<string>(null);
  favouriteCommodities = signal<string[]>([]);
  userId = signal<string>(null);

  constructor(
    private commodityService: CommoditiesService,
    private store: Store<{ user: UserState }>,
    private userService: UserService,
    private snackBar: MatSnackBar
  ) {}

  ngOnInit(): void {
    this.store.select(selectUser).subscribe({
      next: (user) => {
        this.defaultCommodity.set(user.default_commodity);
        this.newDefaultCommodity.setValue(user.default_commodity);

        this.defaultMarketType.set(user.default_market_type);
        this.defaultOrigin.set(user.default_origin);
        this.newDefaultCommodity.setValue(this.defaultCommodity());

        this.userId.set(user._id);

        this.favouriteCommodities.set([...user.favorable_commodities]);
        this.favCommodityCtrl.setValue([...this.favouriteCommodities()]);
      },
    });

    this.commodityService
      .watch({
        market: MarketType[this.defaultMarketType()],
        origin: this.defaultOrigin(),
      })
      .valueChanges.pipe(
        map((res) => {
          const data = res.data.commodities;
          this.defaultOptions.set(data.map((c) => c.commodity).sort());
          this.favouriteOptions.set(data.map((c) => c.commodity).sort());
          return res.data.commodities;
        })
      )
      .subscribe();
  }

  filter(e: Event): void {
    const filterValue = this.input.nativeElement.value.toLowerCase();
    switch (e.type) {
      case 'focus':
        this.filteredOptions = this.defaultOptions();
        break;
      default:
        this.filteredOptions = this.defaultOptions().filter((o) =>
          o.toLowerCase().includes(filterValue)
        );
        break;
    }
  }

  saveNewCommmodity() {
    if (this.newDefaultCommodity.value !== this.defaultCommodity()) {
      this.userService
        .updateDefaultCommodity(this.newDefaultCommodity.value)
        .subscribe({
          next: (res) => {
            // this.store.dispatch(getUserData());
            this.defaultCommodity.set(this.newDefaultCommodity.value);
            this.snackBar.open(
              `${this.newDefaultCommodity.value} set as default commodity`,
              'Close',
              { panelClass: ['blue-snackbar'], duration: 4300 }
            );
            logEvent(this.analytics, LogEventsEnum.user_update);
          },
        });
    }
  }

  // favourite commodities logic
  newFavouriteCommodities() {
    if (this.favCommodityCtrl.value !== this.favouriteCommodities()) {
      this.userService
        .updateFavouriteCommodities([...this.favCommodityCtrl.value])
        .pipe(
          delay(600),
          switchMap((res) => {
            return of(res);
          })
        )
        .subscribe({
          next: (res) => {
            // this.store.dispatch(getUserData());
            this.snackBar.open(`New Favourite Commodities Set`, 'Close', {
              panelClass: ['blue-snackbar'],
              duration: 4300,
            });
            logEvent(this.analytics, LogEventsEnum.user_update);
          },
        });
    }
  }

  ngOnDestroy(): void {
    if (this.favCommodityCtrl.dirty || this.newDefaultCommodity.dirty) {
      this.store.dispatch(getUserData());
    }
  }
}
